import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { BookingRequest } from '@cocoonspace/sdk-js/v2/types/bookingRequest'
import { useCallback, useMemo, useState } from 'react'
import { listSpaceAvailabilities } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import {
	getSpace,
	useGetSpace,
} from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { updateBookingRequest } from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'
import { useRouter } from '~/hooks/use-localized-navigation.hook'

import { useCreateBookingOptions } from '~/domains/booking/hooks/use-create-booking-options.hook'

export const useCreateBookingFromBookingRequest = (
	bookingRequest: BookingRequest,
) => {
	const router = useRouter()

	const [isDialogNoAvailabilitiesOpen, setIsDialogNoAvailabilitiesOpen] =
		useState(false)

	const createBookingOptionsMutation = useCreateBookingOptions()

	const requestedSpaceQuery = useGetSpace(
		bookingRequest.requestedLocation.space_id as string,
		undefined,
		{ query: { enabled: false } },
	)

	const redirectToPayment = useCallback(
		(bookingId: string) => {
			const params = new URLSearchParams({
				bookingRequestId: bookingRequest.id,
				from: 'scheduling',
				guest: bookingRequest.email,
			})

			return router.push(`/booking/${bookingId}/payment?${params}`)
		},
		[bookingRequest.email, bookingRequest.id, router],
	)

	const handleCreateBookingForCocoon = useCallback(async () => {
		if (!bookingRequest.location?.space_ids) return

		const payload = bookingRequest.location.space_ids.map((spaceID) => ({
			day: bookingRequest.day,
			start: bookingRequest.start,
			end: bookingRequest.end,
			space: spaceID,
			type: 'meeting' as const,
		}))

		const availabilities = await listSpaceAvailabilities(payload)

		const availability = availabilities.data.find(
			(a) => a.available && a.space_id,
		)

		if (!availability) {
			return setIsDialogNoAvailabilitiesOpen(true)
		}

		const space = await getSpace(availability.space_id as string)

		createBookingOptionsMutation.mutate(
			{
				bookingOptions: [
					{
						day: bookingRequest.day,
						start: bookingRequest.start,
						end: bookingRequest.end,
					},
				],
				space: space.data,
				bookingRequestID: bookingRequest.id,
			},
			{
				onSuccess: async ({ id }) => {
					await updateBookingRequest(bookingRequest.id, { bookingId: id })
					redirectToPayment(id)
				},
			},
		)
		// oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
	}, [bookingRequest, redirectToPayment, createBookingOptionsMutation.mutate])

	const createBookingAndRedirect = useCallback(async () => {
		if (bookingRequest.requestedLocation.type === 'cocoon') {
			handleCreateBookingForCocoon()
		}
	}, [handleCreateBookingForCocoon, bookingRequest.requestedLocation.type])

	const handleBookAlternative = useCallback(
		async (space: Space) => {
			createBookingOptionsMutation.mutate(
				{
					bookingOptions: [
						{
							day: bookingRequest.day,
							start: bookingRequest.start,
							end: bookingRequest.end,
						},
					],
					space,
					bookingRequestID: bookingRequest.id,
				},
				{
					onSuccess: async ({ id }) => {
						await updateBookingRequest(bookingRequest.id, { bookingId: id })
						setIsDialogNoAvailabilitiesOpen(false)
						redirectToPayment(id)
					},
				},
			)
		},
		// oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
		[bookingRequest, redirectToPayment, createBookingOptionsMutation.mutate],
	)

	return useMemo(
		() => ({
			createBookingAndRedirect,
			isDialogNoAvailabilitiesOpen,
			setIsDialogNoAvailabilitiesOpen,
			requestedSpaceQuery,
			createBookingOptionsMutation,
			handleBookAlternative,
		}),
		[
			createBookingAndRedirect,
			isDialogNoAvailabilitiesOpen,
			requestedSpaceQuery,
			createBookingOptionsMutation,
			handleBookAlternative,
		],
	)
}
